// Tab row + panels. Below $md the row is a native scroller: a mouse has no
// horizontal wheel, so it also drags, and the shared scrollbar track tells the
// customer there is more to reach.
if (!customElements.get("content-tabs")) {
  customElements.define(
    "content-tabs",
    class ContentTabs extends HTMLElement {
      // Below this the thumb reads as a dot rather than a bar.
      static MIN_THUMB = 24;
      // A pointer this far from where it went down is a drag, not a tap.
      static DRAG_THRESHOLD = 3;

      connectedCallback() {
        this.buttons = Array.from(
          this.querySelectorAll("[data-content-tabs-button]")
        );
        this.panels = Array.from(
          this.querySelectorAll("[data-content-tabs-panel]")
        );

        if (this.buttons.length === 0 || this.panels.length === 0) return;

        this._onTabClick = (event) =>
          this._activateTab(event.currentTarget.dataset.tabId);

        this.buttons.forEach((button) => {
          button.addEventListener("click", this._onTabClick);
        });

        // Automatic activation (the tablist pattern): the panel follows the
        // focused tab, so walking the row with the keyboard swaps the content
        // without a second key. Delegated — `focus` alone doesn't bubble.
        this._onTabFocusIn = (event) => {
          const button = event.target.closest("[data-content-tabs-button]");
          if (!button) return;
          // Keyboard focus only. A pointer press focuses the chip as well, and a
          // drag or a swipe that starts on one must not switch tabs; a real tap
          // or click arrives as a click and is activated there.
          if (!button.matches(":focus-visible")) return;

          this._activateTab(button.dataset.tabId);
        };

        this.addEventListener("focusin", this._onTabFocusIn);

        this._initRow();

        // A tab whose rows are all hidden is not rendered, so the tab the section
        // marked active can be missing from the row — the first one that survived
        // takes the state over.
        const active = this.buttons.some((button) =>
          button.classList.contains("active")
        );

        if (!active) this._activateTab(this.buttons[0].dataset.tabId);
      }

      disconnectedCallback() {
        this.buttons?.forEach((button) => {
          button.removeEventListener("click", this._onTabClick);
        });

        this.removeEventListener("focusin", this._onTabFocusIn);

        this.row?.removeEventListener("scroll", this._onScroll);
        this.row?.removeEventListener("pointermove", this._onRowPointerMove);
        window.removeEventListener("scroll", this._onWindowScroll);
        this.row?.removeEventListener("pointerdown", this._onPointerDown);
        this.row?.removeEventListener("click", this._onRowClick, true);
        this.row?.removeEventListener("dragstart", this._onDragStart);
        document.removeEventListener("pointermove", this._onPointerMove);
        document.removeEventListener("pointerup", this._onPointerUp);
        this.rowObserver?.disconnect();
        this.rowObserver = null;
      }

      _initRow() {
        this.row = this.querySelector("[data-content-tabs-row]");
        this.scrollbar = this.querySelector("[data-content-tabs-scrollbar]");
        this.thumb = this.querySelector("[data-content-tabs-scrollbar-drag]");

        if (!this.row) return;

        // Read once — the sync runs per scroll frame, and the direction only
        // changes with a re-render of the section.
        this.rtl = getComputedStyle(this.row).direction === "rtl";

        this._onScroll = () => this._syncOverflow();
        this.row.addEventListener("scroll", this._onScroll, { passive: true });

        if (window.ResizeObserver) {
          this.rowObserver = new ResizeObserver(() => this._syncOverflow());
          this.rowObserver.observe(this.row);
        }

        // A page scroll slides the chips under a resting cursor and the browser
        // hovers whichever one lands there. The row keeps its idle look until the
        // pointer itself moves again; the styles read the class off the row.
        // Both handlers fire per frame while the input lasts, so they check the
        // class before writing it.
        this._onWindowScroll = () => {
          if (this.row.classList.contains("is-page-scrolling")) return;
          this.row.classList.add("is-page-scrolling");
        };
        this._onRowPointerMove = (event) => {
          if (event.pointerType !== "mouse") return;
          if (!this.row.classList.contains("is-page-scrolling")) return;
          this.row.classList.remove("is-page-scrolling");
        };

        window.addEventListener("scroll", this._onWindowScroll, {
          passive: true,
        });
        this.row.addEventListener("pointermove", this._onRowPointerMove, {
          passive: true,
        });

        this._initDrag();
        this._syncOverflow();
      }

      _syncOverflow() {
        if (!this.row) return;

        const maxScroll = this.row.scrollWidth - this.row.clientWidth;
        // Sub-pixel leftovers are not a scrollable row.
        const scrollable = maxScroll > 1;
        // scrollLeft runs negative in RTL, so the travel is read as a distance.
        const scrolled = Math.abs(this.row.scrollLeft);

        this._syncFades(scrollable, scrolled, maxScroll);
        this._syncScrollbar(scrollable, scrolled, maxScroll);
      }

      // The mask only ramps at an end the row can still travel towards, so the
      // fade doubles as the hint that there are more chips that way.
      _syncFades(scrollable, scrolled, maxScroll) {
        const start = scrollable && scrolled > 1 ? "1" : "0";
        const end = scrollable && scrolled < maxScroll - 1 ? "1" : "0";

        this.row.style.setProperty(
          "--content-tabs-fade-left",
          this.rtl ? end : start
        );
        this.row.style.setProperty(
          "--content-tabs-fade-right",
          this.rtl ? start : end
        );
      }

      _syncScrollbar(scrollable, scrolled, maxScroll) {
        if (!this.scrollbar || !this.thumb) return;

        if (!scrollable) {
          this.scrollbar.hidden = true;
          return;
        }

        this.scrollbar.hidden = false;

        const track = this.scrollbar.clientWidth;
        const ratio = this.row.clientWidth / this.row.scrollWidth;
        const thumb = Math.max(track * ratio, ContentTabs.MIN_THUMB);
        const progress = Math.min(scrolled / maxScroll, 1);

        this.thumb.style.width = `${thumb}px`;
        // The offset is a logical one, so RTL needs no sign flipping.
        this.thumb.style.insetInlineStart = `${(track - thumb) * progress}px`;
      }

      _initDrag() {
        let startX = 0;
        let startScroll = 0;
        let dragging = false;
        let dragged = false;

        this._onPointerMove = (event) => {
          if (!dragging) return;

          const delta = event.clientX - startX;
          if (Math.abs(delta) > ContentTabs.DRAG_THRESHOLD) dragged = true;
          this.row.scrollLeft = startScroll - delta;
        };

        this._onPointerUp = () => {
          dragging = false;
          document.removeEventListener("pointermove", this._onPointerMove);
          this.row.classList.remove("is-dragging");
          // The click lands right after this, so the flag is cleared behind it.
          setTimeout(() => {
            dragged = false;
          });
        };

        this._onPointerDown = (event) => {
          // Touch and pen already pan the row natively.
          if (event.pointerType !== "mouse" || event.button !== 0) return;
          if (this.row.scrollWidth <= this.row.clientWidth) return;

          startX = event.clientX;
          startScroll = this.row.scrollLeft;
          dragging = true;
          dragged = false;
          this.row.classList.add("is-dragging");

          document.addEventListener("pointermove", this._onPointerMove);
          document.addEventListener("pointerup", this._onPointerUp, {
            once: true,
          });
        };

        // A drag must not switch the tab it happens to end on.
        this._onRowClick = (event) => {
          if (!dragged) return;

          event.preventDefault();
          event.stopPropagation();
        };

        this._onDragStart = (event) => event.preventDefault();

        this.row.addEventListener("pointerdown", this._onPointerDown);
        this.row.addEventListener("click", this._onRowClick, true);
        this.row.addEventListener("dragstart", this._onDragStart);
      }

      // Brings the picked chip fully into the scrolling row; the row's
      // scroll-padding is what keeps it off the screen edge. `nearest` in both
      // axes so a chip already in view — and the page itself — stays put.
      _revealTab(button) {
        if (!this.row || this.row.scrollWidth <= this.row.clientWidth) return;

        const reduced = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        button.scrollIntoView({
          behavior: reduced ? "auto" : "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }

      _activateTab(tabId) {
        if (!tabId) return;

        this.buttons.forEach((button) => {
          const isActive = button.dataset.tabId === tabId;
          // `active`, not `is-active` — the button is a shared .filter-pill and
          // that is the class its selected state keys on.
          button.classList.toggle("active", isActive);
          button.setAttribute("aria-selected", String(isActive));

          if (isActive) this._revealTab(button);
        });

        this.panels.forEach((panel) => {
          panel.classList.toggle("is-active", panel.dataset.tabId === tabId);
        });
      }
    }
  );
}
